import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';

import { useAccountsPaged } from '@/hooks/useAccounts';
import { approvalListQueryOptions } from '@/hooks/useApprovals';
import { quoteListQueryOptions } from '@/hooks/useQuotes';

const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_GROUP = 4;
const SEARCH_STALE_TIME = 30_000;

export function useGlobalSearch(query: string, includeApprovals: boolean) {
  const [debouncedQuery] = useDebouncedValue(query.trim(), 300);
  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const approvalsEnabled = enabled && includeApprovals;
  const params = { search: debouncedQuery, page: 1, pageSize: RESULTS_PER_GROUP };

  const accounts = useAccountsPaged(params, { enabled });
  const quotes = useQuery({
    ...quoteListQueryOptions(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: SEARCH_STALE_TIME,
  });
  const approvals = useQuery({
    ...approvalListQueryOptions(params),
    enabled: approvalsEnabled,
    placeholderData: keepPreviousData,
    staleTime: SEARCH_STALE_TIME,
  });

  return {
    accounts: enabled ? (accounts.data?.items ?? []) : [],
    quotes: enabled ? (quotes.data?.items ?? []) : [],
    approvals: approvalsEnabled ? (approvals.data?.items ?? []) : [],
    isFetching: enabled && (accounts.isFetching || quotes.isFetching || (approvalsEnabled && approvals.isFetching)),
    isError: enabled && (accounts.isError || quotes.isError || (approvalsEnabled && approvals.isError)),
  };
}
